import React, { useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { platformAPI } from '../../services/platformAPI';
import { useWorkspaceAccess } from '../../hooks/useWorkspaceAccess';
import { isWorkspaceAdmin } from '../../utils/permissions';
import WorkspaceModal from './WorkspaceModal';

export const isPlatformAdminUser = (user) => (user?.roles || []).some((role) => (
  role === 'PLATFORM ADMINISTRATOR' || role === 'SYSTEM ADMINISTRATOR'
));

const rowId = (row) => String(row?._id || row?.id || '');

export function useAdminRecordDelete({
  entity,
  catalog,
  noun = 'record',
  plural,
  reload,
  label = (row) => row?.name || row?.title || row?.label || 'Record',
  note,
  allowRow
}) {
  const user = useSelector((state) => state.auth.user);
  const access = useWorkspaceAccess();
  const deletePermission = {
    customer: 'customers.delete',
    service: 'masters.delete',
    sector: 'masters.delete',
    tenderType: 'masters.delete',
    source: 'masters.delete',
    rate: 'masters.delete',
    activity: 'masters.delete',
    issuer: 'masters.delete',
    partner: 'masters.delete',
    decisionProfile: 'masters.delete',
    diligence: 'masters.delete',
    dropdown: 'masters.delete',
    personnel: 'personnel.delete',
    tender: 'tenders.delete',
    timesheet: 'timesheets.delete',
    document: 'documents.delete',
    security: 'securities.delete',
    credential: 'knowledge.delete',
    project: 'knowledge.delete',
    reusable: 'knowledge.delete',
    user: 'users.delete'
  }[entity];
  const masterRecord = [
    'user', 'customer', 'service', 'sector', 'tenderType', 'source', 'rate', 'activity',
    'issuer', 'partner', 'decisionProfile', 'diligence', 'dropdown'
  ].includes(entity);
  const enabled = isPlatformAdminUser(user)
    || (isWorkspaceAdmin(user?.roles) && masterRecord)
    || (deletePermission && access.allowed([deletePermission]));
  const pluralLabel = plural || `${noun}s`;
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [modalError, setModalError] = useState('');
  const [notice, setNotice] = useState('');
  const [picked, setPicked] = useState([]);

  const ask = (mode, ids, labels = []) => {
    const clean = [...new Set((ids || []).map((id) => String(id || '')).filter(Boolean))];
    if (!clean.length) return;
    setModalError('');
    setPending({ mode, ids: clean, labels: labels.filter(Boolean) });
  };

  const onDelete = (row) => {
    if (allowRow && !allowRow(row)) return;
    ask('one', [rowId(row)], [label(row)]);
  };

  const onRequestDelete = ({ mode, ids, rows }) => {
    const allowed = (rows || []).filter((row) => !allowRow || allowRow(row));
    const nextIds = allowed.length ? allowed.map(rowId) : ids;
    ask(mode, nextIds, allowed.map(label));
  };

  const close = () => {
    if (busy) return;
    setPending(null);
    setModalError('');
  };

  const confirm = async () => {
    if (!pending) return;
    setBusy(true);
    setModalError('');
    try {
      const response = await platformAPI.deleteRecords({
        entity,
        catalog,
        ids: pending.ids
      });
      const kept = response.data?.data?.kept || [];
      setPending(null);
      setPicked([]);
      setNotice(kept.length ? kept.map((item) => item.message).filter(Boolean).join(' ') : '');
      if (reload) await reload();
    } catch (error) {
      setModalError(error.response?.data?.message || 'The records could not be deleted.');
    } finally {
      setBusy(false);
    }
  };

  const wrapActions = (builder) => (row) => {
    const actions = (typeof builder === 'function' ? builder(row) : builder) || [];
    const withoutDelete = actions.filter((action) => action.type !== 'delete');
    if (!enabled || (allowRow && !allowRow(row))) return withoutDelete;
    if (actions.some((action) => action.type === 'delete')) return actions;
    return [...actions, { type: 'delete', label: 'Delete', variant: 'danger' }];
  };

  const toggle = (id) => {
    const key = String(id);
    setPicked((current) => (current.includes(key) ? current.filter((item) => item !== key) : [...current, key]));
  };

  const tableProps = enabled
    ? { showCheckboxes: true, onDelete, onRequestDelete }
    : { showCheckboxes: false };

  const title = !pending
    ? ''
    : pending.mode === 'all'
      ? `Delete all ${pending.ids.length} ${pluralLabel}?`
      : pending.mode === 'selected'
        ? `Delete ${pending.ids.length} selected ${pluralLabel}?`
        : `Delete this ${noun}?`;

  const preview = (pending?.labels || []).slice(0, 8);
  const extra = Math.max(0, (pending?.labels || []).length - preview.length);

  const modal = enabled ? (
    <WorkspaceModal
      show={Boolean(pending)}
      onHide={close}
      title={title}
      description={note || 'Records that already appear on another record stay in the list. The reason is shown after you confirm.'}
      size="sm"
      className="workspace-modal--confirm"
      enableTestFillHotkey={false}
      footer={(
        <>
          <Button variant="outline-secondary" size="sm" onClick={close} disabled={busy}>Cancel</Button>
          <Button variant="danger" size="sm" onClick={confirm} disabled={busy}>
            {busy ? 'Deleting…' : 'Delete'}
          </Button>
        </>
      )}
    >
      {modalError ? <Alert variant="danger" className="mb-2">{modalError}</Alert> : null}
      <p className="mb-0">
        {preview.length ? preview.join(', ') : `${pending?.ids.length || 0} ${pluralLabel}`}
        {extra ? ` and ${extra} more` : ''}
        {' '}will be removed when they are not already used.
      </p>
    </WorkspaceModal>
  ) : null;

  const noticeAlert = notice ? (
    <Alert variant="danger" dismissible onClose={() => setNotice('')}>{notice}</Alert>
  ) : null;

  return {
    enabled,
    entity,
    picked,
    setPicked,
    toggle,
    ask,
    onDelete,
    onRequestDelete,
    tableProps,
    wrapActions,
    modal,
    notice: noticeAlert
  };
}

export function AdminPlainBar({ admin, rows }) {
  if (!admin.enabled) return null;
  const ids = (rows || []).map((row) => String(row._id));
  const allOn = ids.length > 0 && ids.every((id) => admin.picked.includes(id));
  return (
    <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
      <Form.Check
        type="checkbox"
        id={`admin-all-${admin.entity || 'records'}`}
        label="Select all"
        checked={allOn}
        onChange={(event) => admin.setPicked(event.target.checked ? ids : [])}
      />
      <Button size="sm" variant="outline-danger" disabled={!admin.picked.length} onClick={() => admin.ask('selected', admin.picked)}>
        Delete selected
      </Button>
      <Button size="sm" variant="outline-danger" disabled={!ids.length} onClick={() => admin.ask('all', ids, (rows || []).map((row) => row.name || row.label || row.title || row.code || 'Record'))}>
        Delete all
      </Button>
    </div>
  );
}

export function AdminPickCell({ admin, id }) {
  if (!admin.enabled) return null;
  const key = String(id);
  return (
    <td>
      <input
        type="checkbox"
        checked={admin.picked.includes(key)}
        onChange={() => admin.toggle(key)}
        aria-label="Select row"
      />
    </td>
  );
}

export function AdminRowDelete({ admin, id, label }) {
  if (!admin.enabled) return null;
  return (
    <Button size="sm" variant="link" className="text-danger" onClick={() => admin.ask('one', [id], [label])}>
      Delete
    </Button>
  );
}
